import { markRaw } from "vue";
import { authoringPathBasename } from "#bb/lib/pathContract";

const DEFAULT_SCOPE = "default";
const DEFAULT_REPORT_NAME = "report";

// Deterministic client-side cache keyed by site + user + file. Using $fetch
// (via useApiFetch) instead of useFetch avoids Nuxt's payload/AsyncData caching
// lifecycle, which produced flaky `data.value` timing when fetchReports is
// invoked from a watcher (not setup) during rapid `?user=` navigation.
const reportCache = new Map<string, any[]>();
const inFlightReports = new Map<string, Promise<any[]>>();

function normalizeReportFileName(fileName?: string | null): string {
  if (typeof fileName === "string" && fileName.trim().length) {
    return authoringPathBasename(fileName) || DEFAULT_REPORT_NAME;
  }
  return DEFAULT_REPORT_NAME;
}

function getErrorMessage(err: any): string {
  return (
    err?.data?.detail ||
    err?.data?.title ||
    err?.response?._data?.detail ||
    err?.response?._data?.title ||
    err?.message ||
    "API request failed"
  );
}

type ReportsScopeArg =
  | string
  | {
      scope?: string | null;
      initialFileName?: string | null;
    };

function resolveScope(arg?: ReportsScopeArg) {
  if (typeof arg === "string") {
    const trimmed = arg.trim();
    return {
      scope: trimmed.length ? trimmed : DEFAULT_SCOPE,
      initialFileName: DEFAULT_REPORT_NAME,
    } as const;
  }

  const scope = arg?.scope ?? DEFAULT_SCOPE;
  const normalizedScope = typeof scope === "string" && scope.trim().length ? scope.trim() : DEFAULT_SCOPE;
  const initialFileName = normalizeReportFileName(arg?.initialFileName);
  return { scope: normalizedScope, initialFileName } as const;
}

function buildStateKey(scope: string, suffix: string) {
  return `reports:${scope}:${suffix}`;
}

export const useReportsStore = (arg?: ReportsScopeArg) => {
  const { site } = useSite();
  const { scope, initialFileName } = resolveScope(arg);

  // Persistent state scoped per consumer so multiple providers can coexist.
  const reportData = useState<any[] | null>(buildStateKey(scope, "data"), () => null);

  // Shared loading & error flags per scope
  const loading = useState<boolean>(buildStateKey(scope, "loading"), () => false);
  const error = useState<string | null>(buildStateKey(scope, "error"), () => null);
  const reportFileName = useState<string>(buildStateKey(scope, "fileName"), () => initialFileName);
  const lastFetchKey = useState<string | null>(buildStateKey(scope, "lastFetchKey"), () => null);

  function setReportFileName(fileName?: string | null) {
    const normalized = normalizeReportFileName(fileName);
    if (reportFileName.value !== normalized) {
      reportFileName.value = normalized;
      // Invalidate cached data when the target file changes for this scope
      reportData.value = null;
      lastFetchKey.value = null;
    }
  }

  async function fetchReports(userId?: string, options?: { fileName?: string; refresh?: boolean }): Promise<any[]> {
    const effectiveFileName = normalizeReportFileName(options?.fileName ?? reportFileName.value);
    const resolvedSiteId = site.value?.id ?? "";
    // Cache key must distinguish site + user + file so a per-user report never
    // reuses another user's rows (the original navigation bug).
    const requestKey = `${resolvedSiteId || "no-site"}::${userId ?? "__all__"}::${effectiveFileName}`;

    if (!options?.refresh && lastFetchKey.value === requestKey && reportData.value !== null) {
      loading.value = false;
      return reportData.value;
    }

    if (reportFileName.value !== effectiveFileName) {
      reportFileName.value = effectiveFileName;
    }

    loading.value = true;
    error.value = null;

    const params = new URLSearchParams();
    if (userId) params.set("userId", userId);
    if (effectiveFileName) params.set("fileName", effectiveFileName);

    const query = params.toString();
    const headers: Record<string, string> = {};
    if (resolvedSiteId) {
      headers["x-site-id"] = resolvedSiteId;
    }
    if (options?.refresh) {
      headers["x-refresh"] = "true";
    }

    const url = query ? `/v1/reports/users?${query}` : "/v1/reports/users";

    try {
      // Serve from the deterministic client cache unless an authoritative
      // refresh is requested. $fetch returns the real rows on await, so
      // reportData is always populated before the caller reads it.
      if (!options?.refresh && reportCache.has(requestKey)) {
        const cached = reportCache.get(requestKey) ?? [];
        reportData.value = cached;
        lastFetchKey.value = requestKey;
        return cached;
      }

      // De-dupe concurrent identical requests (rapid navigation) so a single
      // network call backs every awaiter and no partial state leaks between them.
      let request = inFlightReports.get(requestKey);
      if (!request || options?.refresh) {
        request = useApiFetch<any[]>(url, { headers })
          .then((result) => {
            const rows = markRaw(Array.isArray(result) ? result : []);
            reportCache.set(requestKey, rows);
            return rows;
          })
          .finally(() => {
            inFlightReports.delete(requestKey);
          });
        inFlightReports.set(requestKey, request);
      }

      const rows = await request;
      reportData.value = rows;
      lastFetchKey.value = requestKey;
      return rows;
    } catch (err: any) {
      error.value = getErrorMessage(err);
      reportData.value = [];
      lastFetchKey.value = null;
      return [];
    } finally {
      loading.value = false;
    }
  }

  return {
    reportData,
    loading,
    error,
    reportFileName,
    setReportFileName,
    fetchReports,
  };
};
